import {
  QueryRelocationsWhereColumn,
  QuerySupplierOfficesOrderByColumn,
  RelocationStatus,
  SortOrder,
  SqlOperator,
} from "@/gql/graphql";
import {
  formatCurrency,
  formatDistance,
} from "@/lib/Formatters/formatCurrency";
import { useTranslations } from "use-intl";
import { Progress } from "@/components/ui/progress";
import dayjs from "dayjs";
import { relocationListQuery } from "@/app/Relocations/GraphQL/relocationListQuery";
import {
  useGqlMutation,
  useSuspenseGqlQuery,
} from "@/lib/GraphQLCodegen/fetcher";
import { useRelocation } from "@/app/Relocations/Hooks/useRelocation";
import { ImoovaImage } from "@/app/Media/Components/Image";
import { Link } from "@/components/catalyst/link";
import { Badge } from "@/components/catalyst/badge";
import { relocationStatusColorMap } from "@/app/Relocations/Utils/relocationStatusColorMap";
import { CalendarIcon, GaugeIcon, PlusIcon } from "lucide-react";
import { StopwatchIcon } from "@radix-ui/react-icons";
import { supplierOfficeListQuery } from "@/app/Offices/GraphQL/supplierOfficeListQuery";
import { addDestinationMutation } from "@/app/Relocations/GraphQL/relocationMutations";
import { GenericForm } from "@/lib/Components/Form/Forms/GenericForm";
import { MultiSelectInput } from "@/lib/Components/Form/Inputs/MultiSelectInput";
import { Button } from "@/components/catalyst/button";
import { useRelocationActions } from "@/app/Relocations/Hooks/useRelocationActions";
import { IconMenuActions } from "@/lib/Components/Actions/IconMenuActions";

type Props = {
  relocationId: string;
};

export function RelocationAdditionalDestinationsGrid({ relocationId }: Props) {
  const relocation = useRelocation(relocationId);
  const t = useTranslations("relocation");
  const { mutateAsync } = useGqlMutation(addDestinationMutation);
  const getActions = useRelocationActions();

  const { data } = useSuspenseGqlQuery(relocationListQuery, {
    first: 50,
    page: 1,
    status: [
      RelocationStatus.Ready,
      RelocationStatus.Paused,
      RelocationStatus.Expired,
      RelocationStatus.Draft,
    ],
    where: {
      AND: [
        {
          column: QueryRelocationsWhereColumn.GroupId,
          value: relocation.group.id,
        },
        {
          column: QueryRelocationsWhereColumn.Id,
          operator: SqlOperator.Neq,
          value: relocationId,
        },
      ],
    },
  });

  const { data: offices } = useSuspenseGqlQuery(supplierOfficeListQuery, {
    supplier_id: relocation.supplier.id,
    page: 1,
    first: 200,
    archived: false,
    orderBy: [
      {
        column: QuerySupplierOfficesOrderByColumn.Name,
        order: SortOrder.Asc,
      },
    ],
  });

  const destinations = data.relocations.data;

  const usedOfficeIds = destinations.map((d) => d.deliveryOffice.id);
  const availableOffices = offices.supplierOffices.data.filter((office) => {
    return (
      office.id !== relocation.departureOffice.id &&
      office.id !== relocation.deliveryOffice.id &&
      !usedOfficeIds.includes(office.id)
    );
  });

  return (
    <div className="mt-6">
      {destinations.length > 0 ? (
        <div className="mb-4">
          <h3 className="mb-4 text-sm font-medium text-gray-700">
            {t("additionalDestinations.title", { count: destinations.length })}
          </h3>
          <div className="grid grid-cols-2 gap-4">
            {destinations.map((dest) => {
              const booked = dest.bookedBookingsCount ?? 0;
              const total = dest.count + booked;
              const percent = total > 0 ? (booked / total) * 100 : 0;
              const daysUntilExpiry = dayjs(dest.available_to_date).diff(
                dayjs(),
                "days",
              );
              const showExpiry =
                daysUntilExpiry >= 0 && dest.status === RelocationStatus.Ready;

              return (
                <div
                  key={dest.id}
                  className="overflow-hidden rounded-lg border border-gray-200 bg-white"
                >
                  {/* Header with image and city */}
                  <div className="flex items-start gap-3 p-3">
                    {dest.ogImage ? (
                      <div className="h-16 w-24 shrink-0 overflow-hidden rounded-md border border-gray-100">
                        <ImoovaImage
                          alt={dest.reference}
                          media={dest.ogImage}
                          className="h-full w-full object-cover"
                        />
                      </div>
                    ) : null}
                    <div className="min-w-0 flex-1">
                      <div className="flex items-start justify-between gap-2">
                        <Link
                          className="font-semibold text-gray-900 hover:text-blue-600"
                          from="/$tenant/$orgId/supplier/{-$supplierId}"
                          to={
                            "/$tenant/$orgId/supplier/{-$supplierId}/relocations/$relocationId"
                          }
                          params={(prev) => ({
                            ...prev,
                            relocationId: dest.id,
                          })}
                        >
                          {dest.deliveryCity.name}
                        </Link>
                        <div className="flex items-center gap-1">
                          <Badge color={relocationStatusColorMap[dest.status]}>
                            {dest.status}
                          </Badge>
                          <IconMenuActions actions={getActions(dest)} />
                        </div>
                      </div>
                      <p className="mt-0.5 text-xs text-gray-500">
                        {dest.reference}
                      </p>
                    </div>
                  </div>

                  {/* Deal info */}
                  <div className="border-t border-gray-100 bg-gray-50 px-3 py-2">
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
                      <span className="flex items-center gap-1">
                        <StopwatchIcon className="h-3 w-3" />
                        {dest.hire_units_allowed} +{" "}
                        {dest.extra_hire_units_allowed}{" "}
                        {t(`hire_unit_type.${dest.hire_unit_type}`)}s
                      </span>
                      <span className="flex items-center gap-1">
                        <GaugeIcon className="h-3 w-3" />
                        {formatDistance(
                          dest.distance_allowed,
                          dest.measurement,
                        )}
                      </span>
                      {showExpiry ? (
                        <span className="flex items-center gap-1">
                          <CalendarIcon className="h-3 w-3" />
                          <span
                            className={
                              daysUntilExpiry < 3
                                ? "font-medium text-red-600"
                                : ""
                            }
                          >
                            {t("additionalDestinations.days", {
                              count: daysUntilExpiry,
                            })}
                          </span>
                        </span>
                      ) : null}
                    </div>

                    {/* Rates */}
                    <div className="mt-1 text-xs text-gray-500">
                      {formatCurrency(dest.hire_unit_rate, dest.currency)} /{" "}
                      {formatCurrency(dest.extra_hire_unit_rate, dest.currency)}{" "}
                      {t("additionalDestinations.extra")}
                    </div>

                    {/* Bookings progress */}
                    {total > 0 ? (
                      <div className="mt-2">
                        <div className="mb-1 flex justify-between text-xs text-gray-500">
                          <span>{t("additionalDestinations.bookings")}</span>
                          <span>
                            {booked} / {total}
                          </span>
                        </div>
                        <Progress
                          value={percent}
                          className="h-1.5 bg-gray-200"
                          indicatorClassName="bg-green-500"
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Add destinations form */}
      {availableOffices.length > 0 ? (
        <div className="rounded-lg border border-gray-200 bg-white p-4">
          <h3 className="mb-3 text-sm font-medium text-gray-700">
            {t("additionalDestinations.addTitle")}
          </h3>
          <GenericForm
            initialValues={{
              ids: [] as string[],
            }}
            onSubmit={async (values, { resetForm }) => {
              await mutateAsync({
                relocationId,
                officeIds: values.ids,
              });
              resetForm();
            }}
          >
            <MultiSelectInput
              name="ids"
              label={t("additionalDestinations.selectOffices")}
              className="col-span-full"
              showSelectAll={false}
              options={availableOffices.map((office) => ({
                label: office.name,
                value: office.id,
              }))}
            />
            <div className="col-span-full">
              <Button type="submit" LeadingIcon={PlusIcon}>
                {t("additionalDestinations.addButton")}
              </Button>
            </div>
          </GenericForm>
        </div>
      ) : null}
    </div>
  );
}
